import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  FaChartLine,
  FaShieldAlt,
  FaRocket,
  FaBrain,
  FaArrowRight,
  FaEnvelope,
  FaCheckCircle,
} from "react-icons/fa";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.1 },
  }),
};

export default function InvestorPage() {
  return (
    <main className="bg-white text-gray-800 font-sans">

      {/* ── Hero ── */}
      <section className="bg-gradient-to-br from-blue-900 to-blue-800 text-white py-24 px-6 xl:px-24">
        <div className="max-w-4xl mx-auto text-center">
          <motion.p
            className="text-sky-300 text-sm font-semibold uppercase tracking-widest mb-4"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          >
            Investor Relations
          </motion.p>
          <motion.h1
            className="text-4xl md:text-5xl font-extrabold mb-6"
            initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
          >
            Investing in the Intelligence Layer of Regulated Industry
          </motion.h1>
          <motion.p
            className="text-blue-200 text-lg max-w-2xl mx-auto mb-8"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}
          >
            XKMINNE is building the operating system for AI-driven regulated manufacturing — a $35B+ market where legacy processes still dominate and the cost of failure is existential.
          </motion.p>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}>
            <a
              href="mailto:founder@xkminne.com"
              className="inline-flex items-center gap-2 bg-white text-blue-800 font-bold px-7 py-3.5 rounded-lg shadow-lg hover:bg-sky-50 transition-colors"
            >
              Request Pitch Deck <FaArrowRight />
            </a>
          </motion.div>
        </div>
      </section>

      {/* ── Market Stats ── */}
      <section className="py-24 px-6 xl:px-24 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <p className="text-sky-600 text-sm font-semibold uppercase tracking-widest mb-2">Market Opportunity</p>
            <h2 className="text-3xl font-bold text-blue-900">A Large, Underserved, and Urgent Market</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8 text-center">
            {[
              { stat: "$35B+", label: "AI & Compliance Tech Market", sub: "Growing at 18% CAGR through 2030" },
              { stat: "98%", label: "Manual Compliance Workflows", sub: "Still operated without intelligent automation" },
              { stat: "2x Faster", label: "Deployment vs. Legacy MES", sub: "XKMINNE's proprietary go-live methodology" },
            ].map((item, i) => (
              <motion.div
                key={i}
                custom={i}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                className="p-8 rounded-2xl bg-gradient-to-br from-sky-50 to-blue-50 border border-blue-100"
              >
                <div className="text-4xl font-extrabold text-blue-800 mb-2">{item.stat}</div>
                <p className="font-semibold text-gray-800 mb-1">{item.label}</p>
                <p className="text-xs text-gray-500">{item.sub}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Investment Thesis ── */}
      <section className="py-24 px-6 xl:px-24 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <p className="text-sky-600 text-sm font-semibold uppercase tracking-widest mb-2">Why XKMINNE</p>
            <h2 className="text-3xl font-bold text-blue-900">The Investment Thesis</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                icon: <FaBrain className="text-2xl text-sky-600" />,
                title: "Proprietary AI Advantage",
                desc: "Our AI models are trained on proprietary regulated manufacturing data — creating a defensible, domain-specific moat that generic AI platforms cannot replicate.",
              },
              {
                icon: <FaShieldAlt className="text-2xl text-emerald-600" />,
                title: "Compliance-First Differentiation",
                desc: "Competitors treat compliance as an afterthought. XKMINNE's compliance-first architecture is the product — making us the natural choice in FDA and ISO-regulated environments.",
              },
              {
                icon: <FaRocket className="text-2xl text-violet-600" />,
                title: "Proven Commercial Traction",
                desc: "Existing clients include global life sciences leaders — Abbott, AbbVie, and ZOLL Respicardia. These deployments validate our model and create expansion revenue opportunities.",
              },
              {
                icon: <FaChartLine className="text-2xl text-rose-500" />,
                title: "Scalable SaaS Trajectory",
                desc: "Our services engagement model generates high-margin, recurring consulting revenue while we build toward a scalable SaaS platform for compliance-driven operational intelligence.",
              },
            ].map((item, i) => (
              <motion.div
                key={i}
                custom={i}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                className="bg-white p-7 rounded-xl border border-gray-100 shadow-sm flex gap-5"
              >
                <div className="p-3 rounded-lg bg-gray-50 h-fit flex-shrink-0">{item.icon}</div>
                <div>
                  <h3 className="font-bold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-sm text-gray-500 leading-relaxed">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Use of Funds ── */}
      <section className="py-24 px-6 xl:px-24 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-14">
            <p className="text-sky-600 text-sm font-semibold uppercase tracking-widest mb-2">Pre-Seed Round</p>
            <h2 className="text-3xl font-bold text-blue-900">What We're Building With Your Investment</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                area: "Product",
                pct: "45%",
                items: ["AI platform core build-out", "MES SaaS module development", "LLM compliance engine"],
              },
              {
                area: "Go-to-Market",
                pct: "30%",
                items: ["Enterprise sales infrastructure", "Channel partner development", "Industry event presence"],
              },
              {
                area: "Operations",
                pct: "25%",
                items: ["Team expansion (engineering + delivery)", "Regulatory certification support", "IP and legal protection"],
              },
            ].map((item, i) => (
              <motion.div
                key={i}
                custom={i}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                className="p-7 rounded-xl bg-gradient-to-br from-sky-50 to-blue-50 border border-blue-100"
              >
                <div className="text-3xl font-extrabold text-blue-800 mb-1">{item.pct}</div>
                <h3 className="font-bold text-gray-900 mb-4">{item.area}</h3>
                <ul className="space-y-2">
                  {item.items.map((point, j) => (
                    <li key={j} className="flex items-start gap-2 text-sm text-gray-600">
                      <FaCheckCircle className="text-emerald-500 mt-0.5 flex-shrink-0" />
                      {point}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-24 bg-blue-900 text-white text-center px-6">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-3xl font-extrabold mb-4">Request Investor Materials</h2>
          <p className="text-blue-200 mb-8">
            We're currently running a pre-seed round. If you're an investor or strategic advisor with experience in AI, supply chain, or life sciences, we'd welcome a direct conversation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="mailto:founder@xkminne.com?subject=XKMINNE%20Pitch%20Deck%20Request"
              className="inline-flex items-center justify-center gap-2 bg-white text-blue-800 font-bold px-8 py-4 rounded-lg shadow-lg hover:bg-sky-50 transition-colors"
            >
              <FaEnvelope /> Request Pitch Deck
            </a>
            <Link
              to="/founder"
              className="inline-flex items-center justify-center border border-white/40 text-white font-semibold px-8 py-4 rounded-lg hover:bg-white/10 transition-colors"
            >
              Meet the Founder
            </Link>
          </div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="bg-gray-900 text-gray-400 py-12 px-6 text-center">
        <span className="text-xl font-extrabold text-white">
          XK<span className="text-sky-400">MINNE</span>
        </span>
        <p className="text-sm mt-3 text-gray-500">
          &copy; {new Date().getFullYear()} XKMINNE Technologies. All rights reserved.
        </p>
      </footer>
    </main>
  );
}
